/**
 * 「貼底意圖」工具（AGUI-1178）：切換到長對話後，讓捲動容器持續貼在內容底部，
 * 直到內容高度穩定、使用者自己動手捲，或超過時間上限為止。
 *
 * 為什麼不用「切換當下捲一次 / 看幾幀」：長對話的高度會在切換後很久才穩定（圖片、表格、
 * 程式碼區塊載入，手機側欄收起動畫、網址列收合造成 clientHeight 改變），任何單次或固定幀數的
 * 捲動都可能在高度又變化之前就收工，畫面因此停在頂端。改成「高度一變就補捲」，
 * 就不必知道是哪個因素改了高度。
 */

/** 高度連續這麼久沒有變化就視為穩定，解除貼底 */
export const STICK_SETTLE_MS = 600;
/** 不論如何都會解除貼底的時間上限 */
export const STICK_MAX_MS = 1500;
/** 貼底結束後，仍繼續留意使用者手勢的時間（供結束後的補捲檢查判斷使用者是否動過畫面） */
export const STICK_WATCH_AFTER_STOP_MS = 2500;
/** 視為「使用者主動操作」的事件，一發生就立刻解除貼底，避免把使用者拉回底部 */
const USER_GESTURE_EVENTS = ['touchstart', 'touchmove', 'wheel', 'pointerdown', 'mousedown', 'keydown'];
/** 捲動位置比上次程式設定的位置往上超過這麼多，且不在捲動極限，就視為使用者自己往上捲（AGUI-1302） */
export const USER_SCROLL_UP_PX = 24;

/**
 * 捲動位置是否比基準位置明顯往上（使用者自己往上捲）。
 * 貼底結束後的補捲檢查用它判斷：手指落在 iframe 上滑動時外層收不到任何手勢事件，
 * 只能從捲動位置本身認出使用者動過畫面（AGUI-1302）。
 *
 * @param {number} currentTop 目前的 scrollTop
 * @param {number|null|undefined} baselineTop 基準位置（程式最後一次捲到的位置）；沒有基準時一律視為沒往上捲
 * @returns {boolean}
 */
export function hasScrolledUpFrom(currentTop, baselineTop) {
  if (typeof baselineTop !== 'number') return false;
  return currentTop < baselineTop - USER_SCROLL_UP_PX;
}

/**
 * 啟動貼底意圖。
 *
 * @param {HTMLElement} container 捲動容器（.messages）
 * @param {object} options
 * @param {() => void} options.scrollToEnd 把容器捲到內容底部（由呼叫端決定是否扣掉底部留白）
 * @param {() => boolean} [options.isAtEnd] 目前是否已在內容底部；提供時，貼底期間只要捲動位置被別的邏輯
 *   （scrollIntoView、瀏覽器還原捲動位置等）拉離底部，就立刻拉回，不必等高度變化。
 *   捲動位置明顯往上移（使用者自己捲，見 onScroll）則一律視為使用者操作而解除，不受此參數影響
 * @param {(reason: string) => void} [options.onStop] 解除時回呼，reason 為 settled/max/user/manual
 * @param {number} [options.settleMs] 高度穩定判定時間
 * @param {number} [options.maxMs] 時間上限
 * @returns {(() => void) & { hasUserInteracted: () => boolean }} 手動解除函式（可重複呼叫）；
 *   hasUserInteracted() 回報貼底開始後（含結束後短時間內）使用者是否碰過畫面
 */
export function startStickToBottom(container, { scrollToEnd, isAtEnd, onStop, settleMs = STICK_SETTLE_MS, maxMs = STICK_MAX_MS }) {
  let stopped = false;
  let settleTimer = null;
  let maxTimer = null;
  let pollTimer = null;
  let watchTimer = null;
  let userInteracted = false;
  let resizeObserver = null;
  let mutationObserver = null;
  const observed = new Set();
  // 上次由程式捲到的位置：之後若明顯往上移，代表是使用者自己捲的（見 onScroll）
  let expectedTop = null;

  // 所有貼底捲動都經過這裡，順便記下捲完的位置
  const scrollToEndTracked = () => {
    scrollToEnd();
    expectedTop = container.scrollTop;
  };

  const stop = (reason = 'manual') => {
    if (stopped) return;
    stopped = true;
    clearTimeout(settleTimer);
    clearTimeout(maxTimer);
    clearInterval(pollTimer);
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    container.removeEventListener('scroll', onScroll);
    // 手勢監聽不在此移除：結束後還要留意一小段時間，讓補捲檢查知道使用者有沒有動過畫面
    if (userInteracted) removeGestureListeners();
    else watchTimer = setTimeout(removeGestureListeners, STICK_WATCH_AFTER_STOP_MS);
    onStop?.(reason);
  };

  function removeGestureListeners() {
    clearTimeout(watchTimer);
    USER_GESTURE_EVENTS.forEach((evt) => document.removeEventListener(evt, onUserGesture, true));
  }

  function onUserGesture() {
    userInteracted = true;
    stop('user');
    removeGestureListeners();
  }

  // 每次高度有動靜就重新計時：連續 settleMs 都沒動靜才算穩定
  const armSettle = () => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => stop('settled'), settleMs);
  };

  // 捲動位置被非使用者的來源改掉（使用者手勢通常會先觸發 stop）：不在底部就拉回。
  // 拉回本身觸發的 scroll 事件此時已在底部，不會形成迴圈。
  //
  // 例外（AGUI-1302）：手指落在 iframe（MCP 互動畫面）上滑動時，觸控事件只送進 iframe，
  // document 收不到，上面的手勢監聽完全不知道使用者在捲。因此改看捲動位置本身：比上次程式捲到的
  // 位置往上移超過 USER_SCROLL_UP_PX、且不在捲動極限，就視為使用者操作。內容變短被瀏覽器夾回時
  // 一定停在捲動極限，以此排除高度變化造成的位移，不會誤判。
  function onScroll() {
    if (stopped) return;
    const atScrollLimit = container.scrollHeight - container.scrollTop - container.clientHeight <= 2;
    if (expectedTop !== null && container.scrollTop < expectedTop - USER_SCROLL_UP_PX && !atScrollLimit) {
      onUserGesture();
      return;
    }
    if (!isAtEnd || isAtEnd()) return;
    scrollToEndTracked();
    armSettle();
  }

  const followHeightChange = () => {
    if (stopped) return;
    scrollToEndTracked();
    armSettle();
  };

  // 掛在 document 的捕獲階段而非容器：手機 WebView 上子元素若擋掉事件冒泡，容器就收不到，
  // 使用者剛切換完就往上滑會被誤拉回底部。
  USER_GESTURE_EVENTS.forEach((evt) => document.addEventListener(evt, onUserGesture, { capture: true, passive: true }));

  // 一律監聽捲動：即使呼叫端沒給 isAtEnd，也要能從捲動位置認出使用者往上捲
  container.addEventListener('scroll', onScroll, { passive: true });
  scrollToEndTracked();
  armSettle();
  maxTimer = setTimeout(() => stop('max'), maxMs);

  if (typeof ResizeObserver !== 'undefined') {
    // 捲動容器自身的框大小不會因內容變長而改變，所以要逐一觀察它的直接子節點
    // （訊息、留白、尾端錨點），並在子節點增減時補掛觀察。
    resizeObserver = new ResizeObserver(followHeightChange);
    const observeChildren = () => {
      Array.from(container.children).forEach((child) => {
        if (!observed.has(child)) {
          observed.add(child);
          resizeObserver.observe(child);
        }
      });
    };
    resizeObserver.observe(container); // clientHeight 改變（側欄收起、網址列收合、鍵盤）
    observeChildren();
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        observeChildren();
        followHeightChange();
      });
      mutationObserver.observe(container, { childList: true });
    }
  } else {
    // 舊 WebView 沒有 ResizeObserver：退回輪詢 scrollHeight
    let lastHeight = container.scrollHeight;
    pollTimer = setInterval(() => {
      if (container.scrollHeight !== lastHeight) {
        lastHeight = container.scrollHeight;
        followHeightChange();
      }
    }, 100);
  }

  const manualStop = () => stop('manual');
  manualStop.hasUserInteracted = () => userInteracted;
  return manualStop;
}
